import type { GetCompanyResponse } from "@/domain/company/companyTypes";
import { usePrivacyMaskStore } from "@/store/privacyMaskStore";
import { useQueryClient } from "@tanstack/react-query";
import { useFeature } from "./useFeature";

/** Shown in place of store and customer data while the mask is on. */
export const MASKED_TEXT = "****";

/**
 * CSS class (css/globals.css) that blurs content while masked. Used where
 * replacing the text is wrong or impossible: form inputs (saving must never
 * write "****"), chat message bodies, photos and media.
 */
export const PRIVACY_BLUR_CLASS = "privacy-blur";

/**
 * Position of a store among the user's real stores (the synthetic "Todas
 * Lojas" option has no id), matched by description and, when given,
 * identifier. Reads the cache only: never triggers a request.
 */
const companyPosition = (
  companies: GetCompanyResponse[] | undefined,
  description: string | null | undefined,
  identifier: string | number | null | undefined
): number | undefined => {
  if (!companies || !description) return undefined;

  const stores = companies.filter(company => !!company.id);
  const wanted =
    identifier === null || identifier === undefined
      ? undefined
      : String(identifier);
  const position = stores.findIndex(
    company =>
      company.description === description &&
      (wanted === undefined || String(company.identifier) === wanted)
  );

  return position === -1 ? undefined : position;
};

/** Only Fidexa staff present screens to other customers. */
const PRIVACY_MASK_FEATURE = "read:back-office";

/**
 * Global privacy mask (eye in the header): hides store and customer
 * identifying data on every dashboard screen so it can be shown in trainings
 * or to other customers. Numbers, dates and amounts stay visible. Without
 * read:back-office the switch does not exist and nothing is ever masked, even
 * if a previous user of the browser left it on.
 *
 * Rules used across the app:
 * - displayed text (names, phones, documents, emails, sale codes): mask();
 * - lists of stores the user must still pick from: maskCompany(…, index)
 *   gives "Empresa 1", "Empresa 2"…;
 * - inputs, message bodies, media: blurClass (value untouched);
 * - copy-to-clipboard and server-generated exports: disabled while isMasked.
 */
export const usePrivacyMask = () => {
  const canToggle = useFeature(PRIVACY_MASK_FEATURE);
  const enabled = usePrivacyMaskStore(state => state.enabled);
  const toggle = usePrivacyMaskStore(state => state.toggle);

  const isMasked = canToggle && enabled;
  const queryClient = useQueryClient();

  return {
    canToggle,
    isMasked,
    toggle,
    /** The value itself, or MASKED_TEXT while masked (empty stays empty). */
    mask: (value: string | null | undefined): string | null | undefined =>
      isMasked && value ? MASKED_TEXT : value,
    /**
     * Store label ("description identifier"). While masked: "Loja N", where N
     * is the store's position in the user's store list (the ["companies"]
     * query already loaded by the dashboard), so the same store reads the same
     * in every list, chip and report row. Falls back to the given list index,
     * then to "Loja ****" when the store is not in that cache.
     */
    maskCompany: (
      description: string | null | undefined,
      identifier?: string | number | null,
      index?: number
    ): string => {
      if (isMasked) {
        const position = companyPosition(
          queryClient.getQueryData<GetCompanyResponse[]>(["companies"]),
          description,
          identifier
        );
        if (position !== undefined) return `Empresa ${position + 1}`;
        return index === undefined
          ? `Empresa ${MASKED_TEXT}`
          : `Empresa ${index + 1}`;
      }
      return [description, identifier]
        .filter(part => part !== null && part !== undefined && part !== "")
        .join(" ");
    },
    /**
     * Person in a pickable list (agents, collaborators): "Colaborador N" while
     * masked, so rows stay distinguishable and actions hit the right person.
     * Pass the index in the FULL list (not a filtered one) to keep N stable.
     */
    maskPerson: (name: string | null | undefined, index: number): string =>
      isMasked ? `Colaborador ${index + 1}` : (name ?? ""),
    /** PRIVACY_BLUR_CLASS while masked, "" otherwise. */
    blurClass: isMasked ? PRIVACY_BLUR_CLASS : "",
  };
};
